import { apiFetch } from './client';

export interface ScraperSiteInput {
  id: string;
  name: string;
  url: string;
  enabled: boolean;
}

export interface ScraperSiteConfig {
  id: string;
  name: string;
  url: string;
  category: string;
  description: string | null;
  icon: string | null;
  official: boolean;
  enabled: boolean;
  jsRequired: boolean;
  requiresLogin: boolean;
  /** Username mascarado — nunca senha. */
  loginUser: string | null;
  credentialsConfigured?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ScraperRunReportBe {
  id: string;
  startedAt: string;
  finishedAt: string;
  totalFound: number;
  sites: Array<{
    siteId: string;
    siteName: string;
    status: 'success' | 'error' | 'partial';
    count: number;
    error?: string;
    duration: number;
  }>;
  triggeredBy: 'manual' | 'schedule';
  warning?: string;
}

export type SiteProgressPhase =
  | 'idle'
  | 'downloading'
  | 'parsing'
  | 'saving'
  | 'enriching'
  | 'done'
  | 'error';

export interface SiteProgress {
  siteId: string;
  siteName: string;
  phase: SiteProgressPhase;
  total: number;
  processed: number;
  pct: number;
  message: string;
  startedAt: string | null;
  finishedAt: string | null;
}

export interface ScraperSchedule {
  id: number;
  enabled: boolean;
  runAt: string;
  updatedAt: string;
}

export interface ScraperMsMonitor {
  scraperReachable: boolean;
  pipelineBusy: boolean;
  gaugePct: number;
  status: Record<string, unknown> | ScraperMsStatus | { available: boolean; error?: string };
  logs: Array<{ at?: string; level?: string; message?: string }>;
  lastWebhookAt: string | null;
  message: string;
}

export interface ScraperMsStatus {
  running: boolean;
  phase: 'idle' | 'collection' | 'enrichment';
  startedAt: string | null;
  elapsedSec: number;
  schedule: { enabled: boolean; runAt: string };
  collection: {
    done: boolean;
    sitesTotal: number;
    sitesProcessed: number;
    cardsFound: number;
    errors: string[];
  };
  enrichment: {
    running: boolean;
    processed: number;
    enriched: number;
    failed: number;
    total: number;
    currentUrl: string | null;
    estimatedRemainingMin: number | null;
  };
}

/**
 * API do scraper via backend autenticado.
 * O browser NÃO chama o scraper-ms diretamente.
 */
export const scraperApi = {
  run: (sites: ScraperSiteInput[], triggeredBy: 'manual' | 'schedule' = 'manual') =>
    apiFetch<ScraperRunReportBe>('/scraper/run', {
      method: 'POST',
      body: { sites, triggeredBy },
    }),

  history: () => apiFetch<unknown[]>('/scraper/history'),

  getProgress: () => apiFetch<SiteProgress[]>('/scraper/progress'),

  getSchedule: () => apiFetch<ScraperSchedule>('/scraper/schedule'),

  updateSchedule: (data: { enabled?: boolean; runAt?: string }) =>
    apiFetch<ScraperSchedule>('/scraper/schedule', { method: 'PUT', body: data }),

  /** Sempre true — pipeline via BE (JWT). Mantido por compatibilidade com a UI. */
  isMsDirectEnabled: () => true,

  trigger: () =>
    apiFetch<{ ok?: boolean; message?: string }>('/scraper/trigger', {
      method: 'POST',
      body: {},
    }),

  triggerForce: () =>
    apiFetch<{ ok?: boolean; message?: string }>('/scraper/trigger/force', {
      method: 'POST',
      body: {},
    }),

  stopJob: () =>
    apiFetch<{ ok?: boolean; message?: string }>('/scraper/job/stop', {
      method: 'POST',
      body: {},
    }),

  getMsMonitor: (logsLimit?: number) => {
    const q = logsLimit != null ? `?logs=${encodeURIComponent(String(logsLimit))}` : '';
    return apiFetch<ScraperMsMonitor>(`/scraper/ms-monitor${q}`);
  },

  reloadMsSchedule: () =>
    apiFetch<{ ok?: boolean; message?: string }>('/scraper/job/reload-schedule', {
      method: 'POST',
      body: {},
    }),

  getMsStatus: () => apiFetch<ScraperMsStatus>('/scraper/ms-status'),

  getSites: () => apiFetch<ScraperSiteConfig[]>('/scraper/sites'),

  seedSites: (sites: Omit<ScraperSiteConfig, 'loginUser' | 'createdAt' | 'updatedAt'>[]) =>
    apiFetch<{ id: string; action: string }[]>('/scraper/sites/seed', {
      method: 'POST',
      body: sites,
    }),

  seedSitesFromCatalog: () =>
    apiFetch<{ id: string; action: string }[]>('/scraper/sites/seed-catalog', {
      method: 'POST',
    }),

  createSite: (data: {
    id?: string;
    name: string;
    url: string;
    category: string;
    description?: string;
    icon?: string;
    official?: boolean;
    enabled?: boolean;
    jsRequired?: boolean;
    requiresLogin?: boolean;
  }) => apiFetch<ScraperSiteConfig>('/scraper/sites', { method: 'POST', body: data }),

  updateSite: (
    id: string,
    data: Partial<
      Pick<
        ScraperSiteConfig,
        | 'name'
        | 'url'
        | 'category'
        | 'description'
        | 'icon'
        | 'official'
        | 'enabled'
        | 'jsRequired'
        | 'requiresLogin'
      >
    >
  ) =>
    apiFetch<ScraperSiteConfig>(`/scraper/sites/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: data,
    }),

  deleteSite: (id: string) =>
    apiFetch<ScraperSiteConfig>(`/scraper/sites/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  saveCredentials: (siteId: string, username: string, password: string) =>
    apiFetch<{
      ok: boolean;
      tested: boolean;
      loginOk: boolean;
      loginMessage: string;
    }>(`/scraper/sites/${encodeURIComponent(siteId)}/credentials`, {
      method: 'PUT',
      body: { username, password },
    }),

  getCredentials: (siteId: string) =>
    apiFetch<{
      credentialsConfigured: boolean;
      hasCredentials: boolean;
      username: string | null;
    }>(`/scraper/sites/${encodeURIComponent(siteId)}/credentials`),
};
